import {
  Box,
  Chip,
  IconButton,
  LinkBehavior,
  Paper,
  SuccessChip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  WarningChip,
} from "@crayon/design-system-react";
import { PriceRuleStatus, ProgramType } from "api/client.generated";
import TableBodyCell from "components/primitives/TableBodyCell";
import TableBodyDateCell from "components/primitives/TableBodyDateCell";
import TableBodySkeleton from "components/primitives/TableBodySkeleton";
import TableHeadSortable, { SortableColumn } from "components/primitives/TableHeadSortable";
import { EditIcon } from "images/MuiIcons";
import { useMemo, useState } from "react";
import AppRoutes from "routes/app-routes";
import DefaultPaginationOptions from "types/defaultPaginationOptions";
import { PriceRuleTableModel } from "types/price-rule-model";
import SortDirection from "types/sort-direction";
import getProgramTypeDisplayName from "utils/program-type";
import compareModelsFn from "utils/sort-fn";

interface PriceRulesTableProps {
  priceRules: PriceRuleTableModel[];
  isLoading: boolean;
}

const PriceRulesTable = ({ priceRules, isLoading }: PriceRulesTableProps) => {
  const [page, setPage] = useState(0);
  const rowsPerPageOptions = useMemo<number[]>(() => DefaultPaginationOptions, []);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [sortBy, setSortBy] = useState<keyof PriceRuleTableModel>("lastModifiedDate");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const onPageChange = (_event: React.MouseEvent<HTMLButtonElement>, newPage: number) => {
    setPage(newPage);
  };

  const onRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const columnsOptions = useMemo<SortableColumn<PriceRuleTableModel>[]>(
    () => [
      { label: "Action", field: "action" },
      { label: "Rate", field: "rateLabel", disableSort: true },
      { label: "Source", field: "source" },
      { label: "Program", field: "program" },
      { label: "Customer", field: "customer" },
      { label: "Product", field: "product" },
      { label: "Status", field: "status", align: "center" },
      { label: "Start Date", field: "startDate" },
      { label: "End Date", field: "endDate" },
      { label: "Modified Date", field: "lastModifiedDate", colSpan: 2 },
    ],
    [],
  );

  const priceRulesToDisplay = useMemo<PriceRuleTableModel[]>(
    () =>
      priceRules
        .sort((a, b) => compareModelsFn(a, b, sortBy, sortDirection))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [priceRules, page, rowsPerPage, sortBy, sortDirection],
  );

  const onSortRequest = (
    newSortField: keyof PriceRuleTableModel,
    newSortDirection: SortDirection,
  ) => {
    setSortBy(newSortField);
    setSortDirection(newSortDirection);
  };

  const getStatusChip = (status: PriceRuleStatus) => {
    switch (status) {
      case PriceRuleStatus.Active:
        return <SuccessChip label={status} />;
      case PriceRuleStatus.Future:
        return <WarningChip label={status} />;
      // Expired OR Unknown
      default:
        return <Chip label={status} />;
    }
  };

  const DataTableBody = (
    <TableBody>
      {priceRulesToDisplay.map((x, i) => (
        <TableRow key={x.id + String(i)} data-price-rule-id={x.id}>
          <TableBodyCell label={x.action} />
          <TableBodyCell label={x.rateLabel} />
          <TableBodyCell label={x.source} />
          <TableBodyCell label={getProgramTypeDisplayName(x.program as ProgramType)} />
          <TableBodyCell label={x.customer} />
          <TableBodyCell label={x.product} />
          <TableCell align="center">{getStatusChip(x.status)}</TableCell>
          <TableBodyDateCell date={x.startDate} />
          <TableBodyDateCell date={x.endDate} />
          <TableBodyDateCell date={x.lastModifiedDate} />
          <TableCell align="right">
            <IconButton
              component={LinkBehavior}
              href={AppRoutes.priceRuleEdit.buildRoute(x.id)}
              color="primary"
              disabled={x.status === PriceRuleStatus.Expired}
            >
              <EditIcon />
            </IconButton>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHeadSortable
          columns={columnsOptions}
          sortBy={sortBy}
          sortDirection={sortDirection}
          defaultSortDirection="desc"
          onSortRequest={onSortRequest}
        />
        {isLoading ? (
          <TableBodySkeleton colsCount={columnsOptions.length} rowsCount={3} />
        ) : (
          DataTableBody
        )}
      </Table>
      {!isLoading && (
        <TablePagination
          component={Box}
          count={priceRules.length}
          page={page}
          onPageChange={onPageChange}
          rowsPerPageOptions={rowsPerPageOptions}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          sx={{ my: 1 }}
        />
      )}
    </TableContainer>
  );
};

export default PriceRulesTable;
